import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Copy, PlugZap, RefreshCw, ShieldQuestion } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";

type ProbeState = "idle" | "running" | "ok" | "fail";

type Probe = {
  label: string;
  state: ProbeState;
  detail: string;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
const OAUTH_CLIENT_ID = import.meta.env.VITE_LOVABLE_OAUTH_CLIENT_ID as string | undefined;

const StateBadge = ({ state }: { state: ProbeState }) => {
  const map: Record<ProbeState, { label: string; variant: "secondary" | "default" | "destructive" | "outline" }> = {
    idle: { label: "Not run", variant: "outline" },
    running: { label: "Checking…", variant: "secondary" },
    ok: { label: "Pass", variant: "default" },
    fail: { label: "Fail", variant: "destructive" },
  };
  const cfg = map[state];
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1 border-b border-border/50 py-2 last:border-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <span className="break-all font-mono text-xs">{value || "—"}</span>
  </div>
);

const AuthDebug = () => {
  const [session, setSession] = useState<string>("checking…");
  const [userEmail, setUserEmail] = useState<string>("");
  const [probes, setProbes] = useState<Probe[]>([
    { label: "Auth service reachable", state: "idle", detail: "" },
    { label: "OAuth discovery metadata", state: "idle", detail: "" },
    { label: "Google provider handshake", state: "idle", detail: "" },
  ]);
  const [log, setLog] = useState<string[]>([]);

  const append = (line: string) =>
    setLog((prev) => [`${new Date().toISOString().slice(11, 19)}  ${line}`, ...prev].slice(0, 60));

  const setProbe = (i: number, patch: Partial<Probe>) =>
    setProbes((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  useEffect(() => {
    supabase.auth.getSession().then(({ data, error }) => {
      if (error) {
        setSession(`error: ${error.message}`);
        return;
      }
      setSession(data.session ? "active" : "none");
      setUserEmail(data.session?.user?.email ?? "");
    });
  }, []);

  const runProbes = async () => {
    setLog([]);

    // 1. Auth service reachable
    setProbe(0, { state: "running", detail: "" });
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/settings`, {
        headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string },
      });
      const body = await res.json().catch(() => ({}));
      const providers = Object.entries(body?.external ?? {})
        .filter(([, on]) => on)
        .map(([name]) => name)
        .join(", ");
      setProbe(0, {
        state: res.ok ? "ok" : "fail",
        detail: res.ok ? `HTTP ${res.status} · enabled providers: ${providers || "none"}` : `HTTP ${res.status}`,
      });
      append(`auth settings → HTTP ${res.status}`);
    } catch (e) {
      setProbe(0, { state: "fail", detail: e instanceof Error ? e.message : String(e) });
      append(`auth settings → ${String(e)}`);
    }

    // 2. Discovery metadata
    setProbe(1, { state: "running", detail: "" });
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/.well-known/openid-configuration`);
      const body = await res.json().catch(() => ({}));
      setProbe(1, {
        state: res.ok ? "ok" : "fail",
        detail: res.ok ? `issuer: ${body?.issuer ?? "unknown"}` : `HTTP ${res.status}`,
      });
      append(`discovery → HTTP ${res.status}`);
    } catch (e) {
      setProbe(1, { state: "fail", detail: e instanceof Error ? e.message : String(e) });
      append(`discovery → ${String(e)}`);
    }
  };

  const probeGoogle = async () => {
    setProbe(2, { state: "running", detail: "" });
    append(`google handshake from origin ${window.location.origin}`);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.redirected) {
        setProbe(2, { state: "ok", detail: "Broker accepted the request and redirected." });
        return;
      }
      if (result.error) {
        const message = String((result.error as { message?: string })?.message ?? result.error);
        setProbe(2, { state: "fail", detail: message });
        append(`google handshake → ${message}`);
        return;
      }
      setProbe(2, { state: "ok", detail: "Tokens returned — session established." });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setProbe(2, { state: "fail", detail: message });
      append(`google handshake → ${message}`);
    }
  };

  const snapshot = () => {
    const text = [
      `origin: ${window.location.origin}`,
      `supabase url: ${SUPABASE_URL}`,
      `project id: ${PROJECT_ID}`,
      `oauth client id: ${OAUTH_CLIENT_ID}`,
      `session: ${session}`,
      ...probes.map((p) => `${p.label}: ${p.state} — ${p.detail}`),
      "--- log ---",
      ...log,
    ].join("\n");
    navigator.clipboard.writeText(text).then(
      () => toast.success("Diagnostics copied"),
      () => toast.error("Couldn't copy — select the text instead"),
    );
  };

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <Helmet>
        <title>Auth debug | WeKIT Career Clarity 360</title>
        <meta name="description" content="Diagnose sign-in and Google OAuth problems with live checks against the auth service." />
      </Helmet>

      <div className="mx-auto w-full max-w-3xl space-y-6">
        <header className="space-y-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <ShieldQuestion className="h-6 w-6 text-primary" aria-hidden />
          </div>
          <h1 className="font-display text-3xl">Auth debug</h1>
          <p className="text-muted-foreground">
            Live checks against the auth service and the OAuth broker, so you can see exactly where sign-in breaks.
          </p>
        </header>

        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg">Environment</CardTitle>
          </CardHeader>
          <CardContent>
            <Row label="Current origin" value={typeof window !== "undefined" ? window.location.origin : ""} />
            <Row label="Redirect URI sent to broker" value={typeof window !== "undefined" ? window.location.origin : ""} />
            <Row label="Auth service URL" value={SUPABASE_URL ?? ""} />
            <Row label="Backend project id" value={PROJECT_ID ?? ""} />
            <Row label="OAuth client id" value={OAUTH_CLIENT_ID ?? ""} />
            <Row label="Session" value={userEmail ? `${session} (${userEmail})` : session} />
          </CardContent>
        </Card>

        <Card className="rounded-3xl">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-lg">Checks</CardTitle>
            <Button onClick={runProbes} variant="secondary" className="min-h-11">
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden /> Run checks
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {probes.map((p, i) => (
              <div key={p.label} className="rounded-2xl border border-border/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">{p.label}</span>
                  <StateBadge state={p.state} />
                </div>
                {p.detail && <p className="mt-2 break-words font-mono text-xs text-muted-foreground">{p.detail}</p>}
                {i === 2 && (
                  <Button onClick={probeGoogle} variant="outline" className="mt-3 min-h-11 w-full">
                    <PlugZap className="mr-2 h-4 w-4" aria-hidden /> Test Google sign-in
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="rounded-3xl">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-lg">Event log</CardTitle>
            <Button onClick={snapshot} variant="ghost" className="min-h-11">
              <Copy className="mr-2 h-4 w-4" aria-hidden /> Copy diagnostics
            </Button>
          </CardHeader>
          <CardContent>
            {log.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing yet. Run the checks above.</p>
            ) : (
              <ol className="space-y-1">
                {log.map((line, i) => (
                  <li key={i} className="break-all font-mono text-xs text-muted-foreground">
                    {line}
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button asChild variant="outline" className="min-h-12 flex-1">
            <Link to="/auth">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden /> Back to sign in
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuthDebug;
